/**
 * Reguły publikacji dotyczące etapów i dróg (model WF: decyzja = wybór drogi).
 *
 * Jedno źródło dla WSZYSTKICH miejsc, które sprawdzają graf przed użyciem. Po stronie serwera
 * reguły woła wspólny rdzeń `apps/api/lib/processes/publish-validation.ts` — dla publikacji
 * procesu tenanta (`apps/api/app/api/processes/[id]/publish/route.ts`, węzły z bazy), zapisu
 * grafu OPUBLIKOWANEGO procesu (`.../[id]/graph/route.ts`, graf z żądania), publikacji szablonu
 * ITE (`apps/api/lib/processes/template-validation.ts`, blueprint z `clientId`) i seeda procesu
 * opublikowanego od razu; po stronie panelu kreator (`PublishValidationIndicator` — podgląd tych
 * samych reguł jeszcze przed zapisem). Czysta funkcja: dostaje uproszczony kształt węzłów
 * i krawędzi, zwraca per kod błędu listę węzłów, które regułę łamią (id węzła z bazy, `clientId`
 * z żądania lub blueprintu albo id węzła kreatora).
 *
 * Reguły (wszystkie blokują publikację, poza ostrzeżeniami z `STAGE_RULE_WARNING_ISSUES`):
 *   - ze startu wychodzi dokładnie jedna krawędź (dowolnego typu) i prowadzi do etapu akceptacji
 *     (`decision`) albo do rozdzielenia (`parallel_split`) — inny kształt silnik odrzuca przy
 *     starcie sprawy (`INVALID_START_ROUTE`), więc taki proces nie dałby się uruchomić; reguła
 *     mówi o tym autorowi już przy publikacji (start → scalenie zgłasza dodatkowo reguła granicy
 *     gałęzi z obiegu równoległego),
 *   - każdy węzeł `end` ma niepustą nazwę (proces kończy się na NAZWANYM etapie końcowym),
 *   - każdy etap akceptacji (`decision`) ma niepustą nazwę — także etap bez dróg,
 *   - każdy etap decyzji ma co najmniej jedną drogę (krawędź znanego typu — etap, którego wyjścia
 *     to same krawędzie nieznanego / wycofanego typu, łamie regułę tak samo jak etap bez wyjść),
 *   - OSTRZEŻENIE (nie blokuje): etap bez drogi bez warunku — przy niespełnionych warunkach
 *     akceptant nie ma dokąd pójść, ale taka luka w warunkach może być zamierzona (źle
 *     uzupełniona kartoteka nie dostaje żadnej decyzji do wyboru),
 *   - drogi domyślne: najwyżej jedna domyślna zwykła ALBO dowolnie wiele domyślnych warunkowych
 *     (w sprawie domyślną jest ta, która się pokazuje); mieszać domyślnej zwykłej z domyślnymi
 *     warunkowymi nie wolno,
 *   - w trybie akceptantów `all` / `majority` domyślna warunkowa jest zakazana (domyślna jest tam
 *     jedna i zwykła — inaczej etap po cichu zmieniałby się w „pierwszy głos rozstrzyga”),
 *     a etap z ≥ 2 drogami MUSI mieć drogę domyślną (bez niej nie ma jak rozstrzygnąć drogi,
 *     gdy głosy się rozchodzą),
 *   - przy ≥ 2 drogach z etapu każda ma niepustą nazwę, a nazwy są unikalne
 *     (bez rozróżniania wielkości liter),
 *   - obieg równoległy ma kształt, który obsługuje silnik (rozdzielenie → gałęzie → jedno własne
 *     scalenie → etap końcowy; bez zagnieżdżeń i bez dróg przekraczających granicę gałęzi) —
 *     5 reguł topologii w `process-parallel-rules.ts`, wołanych stąd na tych samych węzłach
 *     i krawędziach (stąd `targetId` na krawędzi).
 *
 * „Droga" = krawędź znanego typu (`PROCESS_EDGE_TYPES`: zwykła albo warunkowa); krawędź
 * nieznanego typu drogą nie jest. „Droga bez warunku" = zwykła (`unconditional`).
 */

import { PARALLEL_RULE_ISSUES, validateParallelTopology } from './process-parallel-rules';
import { isManualRouteEdgeType, isQuorumApprovalMode, type MultiApproverMode } from './processes';

/**
 * Kody reguł etapów i dróg (kolejność = kolejność w `issues`): 11 kodów — reguła drogi ze startu
 * plus 10 reguł etapów i dróg (jedna z nich, brak drogi bez warunku, to ostrzeżenie —
 * `STAGE_RULE_WARNING_ISSUES`) — potem 5 reguł obiegu równoległego (`PARALLEL_RULE_ISSUES`).
 */
export const STAGE_RULE_ISSUES = [
  'START_NODES_INVALID_ROUTE',
  'END_NODES_MISSING_LABEL',
  'DECISION_NODES_MISSING_LABEL',
  'DECISION_NODES_WITHOUT_ROUTES',
  'DECISION_NODES_MISSING_UNCONDITIONAL_ROUTE',
  // Więcej niż jedna domyślna ZWYKŁA (nazwa kodu zostaje — kontrakt API); kilka warunkowych wolno.
  'DECISION_NODES_MULTIPLE_DEFAULT_ROUTES',
  'DECISION_NODES_MIXED_DEFAULT_ROUTES',
  'DECISION_NODES_CONDITIONAL_DEFAULT_ROUTE',
  'DECISION_NODES_MISSING_DEFAULT_ROUTE',
  'MANUAL_ROUTE_MISSING_LABEL',
  'MANUAL_ROUTE_DUPLICATE_LABEL',
  ...PARALLEL_RULE_ISSUES,
] as const;

export type StageRuleIssue = (typeof STAGE_RULE_ISSUES)[number];

/**
 * Kody NIEBLOKUJĄCE (ostrzeżenia): `validateStageRoutes` je liczy i kreator je pokazuje, ale
 * publikacja procesu tenanta i szablonu ITE ich nie odrzuca (`listBlockingStageRuleIssues`).
 * Dziś jeden: etap bez drogi bez warunku — luka w warunkach może być zamierzona.
 */
export const STAGE_RULE_WARNING_ISSUES: readonly StageRuleIssue[] = [
  'DECISION_NODES_MISSING_UNCONDITIONAL_ROUTE',
];

const STAGE_RULE_WARNING_SET: ReadonlySet<StageRuleIssue> = new Set(STAGE_RULE_WARNING_ISSUES);

/**
 * Nazwa pola w odpowiedzi `422 GRAPH_VALIDATION_FAILED` z listą węzłów łamiących regułę —
 * kreator podświetla po niej konkretne etapy. Serwer wystawia pola tylko dla kodów blokujących
 * (ostrzeżeń w odpowiedzi nie ma). `MANUAL_ROUTE_MISSING_LABEL` zachowuje dotychczasową nazwę
 * pola.
 */
export const STAGE_RULE_RESPONSE_FIELDS = {
  START_NODES_INVALID_ROUTE: 'startNodesWithInvalidRoute',
  END_NODES_MISSING_LABEL: 'endNodesWithoutLabel',
  DECISION_NODES_MISSING_LABEL: 'decisionNodesWithoutLabel',
  DECISION_NODES_WITHOUT_ROUTES: 'decisionNodesWithoutRoutes',
  DECISION_NODES_MISSING_UNCONDITIONAL_ROUTE: 'decisionNodesWithoutUnconditionalRoute',
  DECISION_NODES_MULTIPLE_DEFAULT_ROUTES: 'decisionNodesWithMultipleDefaultRoutes',
  DECISION_NODES_MIXED_DEFAULT_ROUTES: 'decisionNodesWithMixedDefaultRoutes',
  DECISION_NODES_CONDITIONAL_DEFAULT_ROUTE: 'decisionNodesWithConditionalDefaultRoute',
  DECISION_NODES_MISSING_DEFAULT_ROUTE: 'decisionNodesWithoutDefaultRoute',
  MANUAL_ROUTE_MISSING_LABEL: 'decisionNodesWithUnlabeledRoutes',
  MANUAL_ROUTE_DUPLICATE_LABEL: 'decisionNodesWithDuplicateRouteLabels',
  PARALLEL_JOIN_EXIT_NOT_END: 'parallelJoinsWithInvalidExit',
  PARALLEL_SPLIT_EXIT_NOT_STAGE: 'parallelSplitsWithInvalidExit',
  PARALLEL_SPLIT_NESTED: 'nestedParallelSplits',
  PARALLEL_SPLIT_JOIN_MISMATCH: 'parallelNodesWithJoinMismatch',
  PARALLEL_BRANCH_BOUNDARY: 'nodesCrossingBranchBoundary',
} as const satisfies Record<StageRuleIssue, string>;

/** Nazwa pola odpowiedzi 422 z listą węzłów łamiących regułę (literały z `STAGE_RULE_RESPONSE_FIELDS`). */
export type StageRuleResponseField = (typeof STAGE_RULE_RESPONSE_FIELDS)[StageRuleIssue];

/**
 * Kody błędów przed użyciem grafu spoza reguł etapów i dróg: start, koniec i akceptanci etapów
 * (kolejność = kolejność w `issues` odpowiedzi 422).
 */
export const PUBLISH_GRAPH_BASIC_ISSUES = [
  'MISSING_START_NODE',
  'MULTIPLE_START_NODES',
  'MISSING_END_NODE',
  'DECISION_NODES_MISSING_APPROVER',
] as const;

export type PublishGraphBasicIssue = (typeof PUBLISH_GRAPH_BASIC_ISSUES)[number];

/**
 * Wszystkie kody blokujące publikację procesu i zapis opublikowanego procesu — te, które wracają
 * w `issues` odpowiedzi `422 GRAPH_VALIDATION_FAILED` (serwer: `lib/processes/publish-validation.ts`,
 * panel: `PublishIssue`). Ostrzeżenia (`STAGE_RULE_WARNING_ISSUES`) w `issues` nie wracają.
 */
export type PublishGraphIssue = PublishGraphBasicIssue | StageRuleIssue;

/** Typy węzłów, do których może prowadzić jedyna droga ze startu (pierwszy krok sprawy). */
const START_TARGET_TYPES: ReadonlySet<string> = new Set(['decision', 'parallel_split']);

export interface StageRuleNode {
  /** Id węzła z bazy / `clientId` z blueprintu / id węzła kreatora — wraca w wyniku bez zmian. */
  id: string;
  nodeType: string;
  label?: string | null;
  /**
   * Tryb akceptantów etapu z konfiguracji (`approverConfig.mode ?? 'any'`) — tak, jak liczy go
   * silnik, także ukryty tryb przeniesiony po zmianie typu akceptantów. `undefined`, gdy
   * konfiguracja akceptantów jest niekompletna / niepoprawna (`isApproverConfigComplete`, serwer:
   * schemat konfiguracji) albo bez typu — wtedy reguły drogi domyślnej zależne od trybu (wymóg
   * domyślnej, zakaz domyślnej warunkowej) dla etapu nie odpalają (publikację i tak blokuje brak
   * akceptantów).
   */
  approvalMode?: MultiApproverMode;
}

export interface StageRuleEdge {
  sourceId: string;
  /** Cel krawędzi (te same id co w `StageRuleNode.id`) — potrzebny regułom obiegu równoległego. */
  targetId: string;
  edgeType: string;
  label?: string | null;
  isDefault?: boolean;
}

/** Per kod błędu: id węzłów łamiących regułę (puste = reguła spełniona). */
export type StageRuleViolations = Record<StageRuleIssue, string[]>;

function isBlank(value: string | null | undefined): boolean {
  return value === null || value === undefined || value.trim() === '';
}

function isUnconditionalRoute(edgeType: string): boolean {
  return edgeType === 'unconditional';
}

export function validateStageRoutes(
  nodes: readonly StageRuleNode[],
  edges: readonly StageRuleEdge[],
): StageRuleViolations {
  const violations: StageRuleViolations = {
    START_NODES_INVALID_ROUTE: [],
    END_NODES_MISSING_LABEL: [],
    DECISION_NODES_MISSING_LABEL: [],
    DECISION_NODES_WITHOUT_ROUTES: [],
    DECISION_NODES_MISSING_UNCONDITIONAL_ROUTE: [],
    DECISION_NODES_MULTIPLE_DEFAULT_ROUTES: [],
    DECISION_NODES_MIXED_DEFAULT_ROUTES: [],
    DECISION_NODES_CONDITIONAL_DEFAULT_ROUTE: [],
    DECISION_NODES_MISSING_DEFAULT_ROUTE: [],
    MANUAL_ROUTE_MISSING_LABEL: [],
    MANUAL_ROUTE_DUPLICATE_LABEL: [],
    ...validateParallelTopology(nodes, edges),
  };

  const outgoingBySource = new Map<string, StageRuleEdge[]>();
  for (const edge of edges) {
    const list = outgoingBySource.get(edge.sourceId) ?? [];
    list.push(edge);
    outgoingBySource.set(edge.sourceId, list);
  }
  const typeById = new Map<string, string>(nodes.map((n) => [n.id, n.nodeType]));

  for (const node of nodes) {
    if (node.nodeType === 'start') {
      // Liczymy WSZYSTKIE krawędzie ze startu, bez względu na typ — silnik też ich nie rozróżnia.
      // Cel spoza grafu (brak węzła o tym id) łamie regułę tak samo jak cel złego typu.
      const outgoing = outgoingBySource.get(node.id) ?? [];
      const onlyRoute = outgoing.length === 1 ? outgoing[0] : undefined;
      const targetType = onlyRoute === undefined ? undefined : typeById.get(onlyRoute.targetId);
      if (targetType === undefined || !START_TARGET_TYPES.has(targetType)) {
        violations.START_NODES_INVALID_ROUTE.push(node.id);
      }
      continue;
    }
    if (node.nodeType === 'end') {
      if (isBlank(node.label)) violations.END_NODES_MISSING_LABEL.push(node.id);
      continue;
    }
    if (node.nodeType !== 'decision') continue;

    // Przed sprawdzeniem wyjść — etap bez dróg też musi mieć nazwę.
    if (isBlank(node.label)) violations.DECISION_NODES_MISSING_LABEL.push(node.id);

    const outgoing = outgoingBySource.get(node.id) ?? [];
    const routes = outgoing.filter((e) => isManualRouteEdgeType(e.edgeType));
    // Etap bez wyjść ORAZ etap, którego wyjścia to same krawędzie nieznanego / wycofanego typu
    // (nie są drogami): akceptant nie ma czego wybrać, więc blokuje publikację. Brak drogi bez
    // warunku (ostrzeżenie niżej) nie odpala — przy zerze dróg byłby tylko szumem.
    if (routes.length === 0) {
      violations.DECISION_NODES_WITHOUT_ROUTES.push(node.id);
      continue;
    }

    // Ostrzeżenie (STAGE_RULE_WARNING_ISSUES) — etap z samymi drogami warunkowymi wolno opublikować.
    if (!routes.some((e) => isUnconditionalRoute(e.edgeType))) {
      violations.DECISION_NODES_MISSING_UNCONDITIONAL_ROUTE.push(node.id);
    }

    const defaults = routes.filter((e) => e.isDefault === true);
    const plainDefaults = defaults.filter((e) => isUnconditionalRoute(e.edgeType));
    const conditionalDefaults = defaults.filter((e) => e.edgeType === 'conditional');
    const isQuorumMode = isQuorumApprovalMode(node.approvalMode);
    // Domyślna zwykła — najwyżej jedna; domyślnych warunkowych może być wiele (w sprawie domyślną
    // jest ta, która się pokazuje), ale nie razem z domyślną zwykłą.
    if (plainDefaults.length > 1) {
      violations.DECISION_NODES_MULTIPLE_DEFAULT_ROUTES.push(node.id);
    }
    if (plainDefaults.length >= 1 && conditionalDefaults.length >= 1) {
      violations.DECISION_NODES_MIXED_DEFAULT_ROUTES.push(node.id);
    }
    if (conditionalDefaults.length > 0 && isQuorumMode) {
      violations.DECISION_NODES_CONDITIONAL_DEFAULT_ROUTE.push(node.id);
    }

    if (routes.length < 2) continue; // 1 droga = routing automatyczny, nazwa i domyślna zbędne

    if (isQuorumMode && defaults.length === 0) {
      violations.DECISION_NODES_MISSING_DEFAULT_ROUTE.push(node.id);
    }

    if (routes.some((e) => isBlank(e.label))) {
      violations.MANUAL_ROUTE_MISSING_LABEL.push(node.id);
    }
    const seen = new Set<string>();
    let hasDuplicate = false;
    for (const route of routes) {
      const key = route.label?.trim().toLocaleLowerCase('pl');
      if (!key) continue; // pusta nazwa jest osobnym błędem (MANUAL_ROUTE_MISSING_LABEL)
      if (seen.has(key)) hasDuplicate = true;
      seen.add(key);
    }
    if (hasDuplicate) violations.MANUAL_ROUTE_DUPLICATE_LABEL.push(node.id);
  }

  return violations;
}

/**
 * Kody reguł, dla których choć jeden węzeł łamie regułę — w stałej kolejności. Razem
 * z ostrzeżeniami (`STAGE_RULE_WARNING_ISSUES`); do blokady publikacji —
 * `listBlockingStageRuleIssues`.
 */
export function listStageRuleIssues(violations: StageRuleViolations): StageRuleIssue[] {
  return STAGE_RULE_ISSUES.filter((code) => violations[code].length > 0);
}

/** Jak `listStageRuleIssues`, ale bez ostrzeżeń — tylko kody, które blokują publikację. */
export function listBlockingStageRuleIssues(violations: StageRuleViolations): StageRuleIssue[] {
  return listStageRuleIssues(violations).filter((code) => !STAGE_RULE_WARNING_SET.has(code));
}
